import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Shield, MessageCircle, Trash2, Info } from 'lucide-react';
import { Match, Team, Player, Tournament } from '../../types';
import { formatPairName } from '../../lib/utils';
import { PairAvatar } from '../common/PairAvatar';
import { getMatchTemporalState } from '../../lib/matchmakingUtils';

interface AdminPendingMatchCardProps {
  key?: any;
  m: Match;
  t1: Team;
  t2: Team;
  players?: Player[];
  onSave: (m: Match, s1: string, s2: string, s3: string, winnerId: string) => Promise<void>;
  onDelete: () => void;
  mode?: 'admin' | 'player';
  readOnly?: boolean;
  onMatchClick?: (m: Match) => void;
  tournament?: Tournament;
  onUpdateDateTime?: (matchId: string, date: string, time: string) => Promise<void>;
  onPlayerAvatarClick?: (playerId: string) => void;
}

export function AdminPendingMatchCard({ m, t1, t2, players = [], onSave, onDelete, mode = 'admin', readOnly = false, onMatchClick, tournament, onUpdateDateTime, onPlayerAvatarClick }: AdminPendingMatchCardProps) {
  const [s1, setS1] = useState(m.s1 || m.reportedResult?.s1 || '');
  const [s2, setS2] = useState(m.s2 || m.reportedResult?.s2 || '');
  const [s3, setS3] = useState(m.s3 || m.reportedResult?.s3 || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [localError, setLocalError] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(m.status === 'completed');
  const isCompleted = m.status === 'completed';

  const [matchDate, setMatchDate] = useState(m.date || '');
  const [matchTime, setMatchTime] = useState(m.time || '');

  useEffect(() => {
    setMatchDate(m.date || '');
    setMatchTime(m.time || '');
  }, [m.date, m.time]);

  // New state for 0-7 picker
  type SetId = 1 | 2 | 3;
  type TeamId = 1 | 2;
  const [activeBox, setActiveBox] = useState<{set: SetId, team: TeamId} | null>(null);
  const [hoveredTeam, setHoveredTeam] = useState<TeamId | null>(null);

  let matchStateLabel = '';
  if (tournament) {
    const { matchState } = getMatchTemporalState(m, tournament);
    if (matchState === 'activo') matchStateLabel = 'ACTIVO';
    else if (matchState === 'inactivo') matchStateLabel = 'INACTIVO';
    else if (matchState === 'demorado') matchStateLabel = 'DEMORADO';
    else if (matchState === 'en_proceso') matchStateLabel = 'EN PROCESO';
    else if (matchState === 'finalizado') matchStateLabel = 'FINALIZADO';
  } else {
    matchStateLabel = isCompleted ? 'FINALIZADO' : (m.status === 'disputed' ? 'EN DISPUTA' : 'PENDIENTE');
  }

  const getScore = (setRaw: string | undefined, team: TeamId) => {
    if (!setRaw) return '';
    const parts = setRaw.split('-');
    return team === 1 ? parts[0] || '' : parts[1] || '';
  };

  const getWinnerOfSet = (setRaw: string) => {
    if (!setRaw) return null;
    const [t1score, t2score] = setRaw.split('-').map(Number);
    if (isNaN(t1score) || isNaN(t2score)) return null;
    if (t1score > t2score) return 1;
    if (t2score > t1score) return 2;
    return null;
  };

  const rs1 = m.reportedResult?.s1;
  const rs2 = m.reportedResult?.s2;
  const rs3 = m.reportedResult?.s3;
  const rs1Winner = getWinnerOfSet(rs1 || '');
  const rs2Winner = getWinnerOfSet(rs2 || '');
  const rs3Winner = getWinnerOfSet(rs3 || '');
  const rT1Wins = (rs1Winner === 1 ? 1 : 0) + (rs2Winner === 1 ? 1 : 0) + (rs3Winner === 1 ? 1 : 0);
  const rT2Wins = (rs1Winner === 2 ? 1 : 0) + (rs2Winner === 2 ? 1 : 0) + (rs3Winner === 2 ? 1 : 0);
  const reportedWinnerTeamName = rT1Wins > rT2Wins ? t1.name : (rT2Wins > rT1Wins ? t2.name : '');
  const reportedScore = [rs1, rs2, rs3].filter(Boolean).join(', ');

  // Determine if Set 3 is needed
  const s1Winner = getWinnerOfSet(s1);
  const s2Winner = getWinnerOfSet(s2);
  const showSet3 = s1Winner && s2Winner && s1Winner !== s2Winner;
  
  // Clear s3 if it shouldn't be shown anymore
  useEffect(() => {
    if (!isCompleted && s1Winner && s2Winner && s1Winner === s2Winner && s3 !== '') {
      setS3('');
    }
  }, [s1Winner, s2Winner, s3, isCompleted]);

  useEffect(() => {
    if (!activeBox) {
      setS1(m.s1 || m.reportedResult?.s1 || '');
      setS2(m.s2 || m.reportedResult?.s2 || '');
      setS3(m.s3 || m.reportedResult?.s3 || '');
    }
  }, [m.s1, m.s2, m.s3, m.reportedResult?.s1, m.reportedResult?.s2, m.reportedResult?.s3]);

  const handleNumberSelect = (num: number) => {
    if (!activeBox || isCompleted) return;
    const { set, team } = activeBox;
    
    // Update score
    const currentScoreStr = set === 1 ? s1 : set === 2 ? s2 : s3;
    const currentParts = currentScoreStr.split('-');
    const newT1 = team === 1 ? num.toString() : (currentParts[0] || '');
    const newT2 = team === 2 ? num.toString() : (currentParts[1] || '');
    const newScore = `${newT1}-${newT2}`;

    if (set === 1) setS1(newScore);
    else if (set === 2) setS2(newScore);
    else setS3(newScore);

    // Auto-advance logic (requested sequence: A1 -> B1 -> A2 -> B2 -> A3 -> B3)
    const otherTeam: TeamId = team === 1 ? 2 : 1;
    const currentSetScore = set === 1 ? s1 : set === 2 ? s2 : s3;
    const otherVal = getScore(currentSetScore, otherTeam);

    if (otherVal === '') {
      // If the opponent in the SAME set is empty, always go there first
      setActiveBox({ set, team: otherTeam });
    } else {
      // Both scores for the current set are filled.
      
      // New Rule: Validation of Padel Set Congruency
      // If after editing the current cell, the set result is invalid for Padel,
      // stay in the same set but jump to the opponent's cell.
      const t1Val = team === 1 ? num : parseInt(getScore(currentSetScore, 1) || '0');
      const t2Val = team === 2 ? num : parseInt(getScore(currentSetScore, 2) || '0');
      const setStr = `${t1Val}-${t2Val}`;
      const isInvalid = !isValidPadelSet(setStr);

      if (isInvalid) {
        setActiveBox({ set, team: otherTeam });
        return;
      }

      // If valid, move to next or close (Smart Edit Mode)
      if (set === 1) {
        const nextSetScore = getScore(s2, otherTeam);
        if (nextSetScore === '') {
          setActiveBox({ set: 2, team: otherTeam });
        } else {
          setActiveBox(null);
        }
      } else if (set === 2) {
        const s1W = getWinnerOfSet(s1);
        const s2W = t1Val > t2Val ? 1 : (t2Val > t1Val ? 2 : null);

        if (s1W && s2W && s1W !== s2W) {
          const s3NextScore = getScore(s3, otherTeam);
          if (s3NextScore === '') {
            setActiveBox({ set: 3, team: otherTeam });
          } else {
            setActiveBox(null);
          }
        } else {
          setActiveBox(null);
        }
      } else {
        setActiveBox(null); // Finished set 3
      }
    }
  };

  const isValidPadelSet = (str: string) => {
    if (!str) return false;
    const [t1score, t2score] = str.split('-').map(Number);
    if (isNaN(t1score) || isNaN(t2score)) return false;
    if (t1score === 6 && t2score <= 4) return true;
    if (t2score === 6 && t1score <= 4) return true;
    if (t1score === 7 && (t2score === 5 || t2score === 6)) return true;
    if (t2score === 7 && (t1score === 5 || t1score === 6)) return true;
    // Super tie-break (10+) or match tie-break could be here, but for now standard sets
    return false;
  };

  const handleConfirm = async () => {
    if (isCompleted) return;
    // Validation
    if (!isValidPadelSet(s1)) {
      setLocalError('⚠️ El Set 1 tiene un resultado inválido para Padel.');
      setTimeout(() => setLocalError(''), 4000);
      setActiveBox({ set: 1, team: 1 });
      return;
    }
    if (!isValidPadelSet(s2)) {
      setLocalError('⚠️ El Set 2 tiene un resultado inválido para Padel.');
      setTimeout(() => setLocalError(''), 4000);
      setActiveBox({ set: 2, team: 1 });
      return;
    }

    if (showSet3) {
      if (!s3 || !isValidPadelSet(s3)) {
        setLocalError('⚠️ Debes completar el Set 3 correctamente (están 1 a 1).');
        setTimeout(() => setLocalError(''), 4000);
        setActiveBox({ set: 3, team: 1 });
        return;
      }
    }

    if (m.status === 'disputed') {
      setConfirmSave(true);
      return;
    }
    
    await executeSave();
  };

  const executeSave = async () => {
    setConfirmSave(false);
    if (isCompleted) return;

    // Determine winner
    const t1Wins = (s1Winner === 1 ? 1 : 0) + (s2Winner === 1 ? 1 : 0) + (getWinnerOfSet(s3) === 1 ? 1 : 0);
    const t2Wins = (s1Winner === 2 ? 1 : 0) + (s2Winner === 2 ? 1 : 0) + (getWinnerOfSet(s3) === 2 ? 1 : 0);
    const calculatedWinnerId = t1Wins > t2Wins ? m.team1Id : m.team2Id;

    setIsSaving(true);
    
    // Mostrar aviso global de "Resultado Cargado" INMEDIATAMENTE para dar tranquilidad al usuario
    const el = document.createElement('div');
    el.className = "fixed top-12 left-1/2 -translate-x-1/2 z-[999999] bg-[#1a1c23]/95 backdrop-blur-md text-emerald-400 px-6 py-4 rounded-2xl font-black text-[11px] md:text-sm shadow-[0_10px_40px_rgba(16,185,129,0.3)] flex items-center gap-3 animate-in slide-in-from-top-10 fade-in duration-300 border border-emerald-500/20 uppercase tracking-widest text-center";
    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 animate-bounce"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg><span>Resultado Cargado</span>`;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity 0.3s ease';
      setTimeout(() => el.remove(), 300);
    }, 4000);

    try {
      await onSave(m, s1, s2, s3, calculatedWinnerId);
      setIsSuccess(true);
    } catch (e) {
      console.error('Error al guardar', e);
      setIsSaving(false);
      setIsSuccess(false);
    } finally {
      setIsSaving(false);
    }
  };

  const renderBox = (setNum: SetId, teamNum: TeamId) => {
    const isVisible = setNum !== 3 || showSet3 || (s3 !== '');
    if (!isVisible) return <div className="w-7 h-7 md:w-9 md:h-9 border border-dashed border-white/15 rounded-lg opacity-35 bg-white/5 pointer-events-none transition-all duration-300" />;

    const isActive = activeBox?.set === setNum && activeBox?.team === teamNum;
    const val = getScore(setNum === 1 ? s1 : setNum === 2 ? s2 : s3, teamNum);

    return (
      <div
        onClick={() => {
          if (isCompleted) return;
          if (activeBox?.set === setNum && activeBox?.team === teamNum) {
            setActiveBox(null);
          } else {
            setActiveBox({ set: setNum, team: teamNum });
          }
        }}
        className={`w-7 h-7 md:w-9 md:h-9 bg-white/5 rounded-lg font-black text-center flex items-center justify-center transition-all text-xs md:text-sm relative z-[150] ${
          isCompleted 
            ? 'border border-white/5 opacity-80 cursor-default'
            : isActive
              ? 'border-2 theme-primary-border bg-theme-primary/20 shadow-lg scale-110 cursor-pointer'
              : 'border border-white/10 hover:border-white/30 cursor-pointer'
        }`}
      >
        <span className={val !== '' ? 'text-white' : 'text-white/20'}>{val !== '' ? val : '-'}</span>
      </div>
    );
  };

  return (
    <div className={`w-full max-w-[460px] mx-auto bg-[#1e293b]/70 backdrop-blur-md p-3 md:p-4 rounded-[20px] border transition-all relative shadow-xl ${isCompleted ? 'theme-primary-border opacity-60' : 'border-white/5'}`}>
      
      {!isCompleted && matchStateLabel && (
        <div className="absolute top-2 left-2 z-20">
          <span className={`px-2 py-1 rounded-lg border text-[8px] font-black uppercase tracking-widest flex items-center gap-1 ${
            matchStateLabel === 'ACTIVO' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            matchStateLabel === 'INACTIVO' ? 'bg-gray-500/10 text-gray-400 border-gray-500/20' :
            matchStateLabel === 'DEMORADO' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
            matchStateLabel === 'EN PROCESO' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
            'bg-black/60 text-white/50 border-white/10'
          }`}>
            {matchStateLabel}
          </span>
        </div>
      )}
      {isCompleted && !readOnly && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-[1px] animate-in fade-in duration-500">
          <div className="theme-primary-bg text-black px-5 py-2 rounded-full font-black uppercase text-[10px] tracking-[0.2em] shadow-lg flex items-center gap-2 border-2 border-black/10">
             <CheckCircle2 size={14} className="animate-bounce" /> Resultado Publicado
          </div>
        </div>
      )}

      {readOnly && (
        <div className="absolute top-2 left-2 z-20">
          <span className="px-2 py-1 bg-black/60 text-white/50 rounded-lg border border-white/10 text-[9px] font-bold uppercase tracking-widest flex items-center gap-1">
            <Shield size={10} /> Disputa Resuelta
          </span>
        </div>
      )}

      {!isCompleted && !readOnly && (m.status === 'disputed' || !!m.impugnedAt) && (
        <div className="absolute -top-3 -right-3 z-30 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded shadow-[0_4px_15px_rgba(0,0,0,0.5)] border border-white/20 text-white bg-red-500 shadow-red-500/40">
          En Disputa
        </div>
      )}

      {/* Date and Time Fields at the Top */}
      {onUpdateDateTime && !isCompleted && !readOnly && mode !== 'player' && tournament?.managementMode === 'express' && (
        <div className="flex justify-center items-center gap-3 w-full pt-1 pb-3 mt-0 mb-0 relative z-20">
          <div className="relative group/date">
            <input 
              type="date"
              value={matchDate}
              onChange={(e) => {
                setMatchDate(e.target.value);
                onUpdateDateTime(m.id, e.target.value, matchTime).catch(()=>{});
              }}
              className="w-full sm:w-[145px] bg-white/5 border border-transparent hover:border-white/10 rounded-xl px-3 py-1.5 text-[11px] md:text-xs font-black text-white text-center outline-none focus:border-theme-primary/50 transition-all [color-scheme:dark] cursor-pointer shadow-inner [&::-webkit-calendar-picker-indicator]:scale-150 [&::-webkit-calendar-picker-indicator]:opacity-80 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
            />
          </div>
          <div className="relative group/time">
            <input 
              type="time"
              value={matchTime}
              onChange={(e) => {
                setMatchTime(e.target.value);
                onUpdateDateTime(m.id, matchDate, e.target.value).catch(()=>{});
              }}
              className="w-full sm:w-[95px] bg-white/5 border border-transparent hover:border-white/10 rounded-xl px-3 py-1.5 text-[11px] md:text-xs font-black text-white text-center outline-none focus:border-theme-primary/50 transition-all [color-scheme:dark] cursor-pointer shadow-inner [&::-webkit-calendar-picker-indicator]:scale-125 [&::-webkit-calendar-picker-indicator]:opacity-80 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
            />
          </div>
        </div>
      )}

      <div className="relative flex w-full items-center justify-center gap-6 mt-1 mb-2 px-1">
        {/* Chat / Message Button on Left (Vertically aligned with Avatars) */}
        {onMatchClick && (
          <button
            onClick={() => onMatchClick(m)}
            className="absolute left-1 px-2.5 py-1.5 bg-theme-primary/10 hover:bg-theme-primary/20 text-theme-primary rounded-xl transition-all border border-theme-primary/20 hover:scale-105 active:scale-95 z-20 flex items-center gap-1.5 shadow-lg"
            title="Ver Chat / Detalles"
          >
            <MessageCircle size={14} className="shrink-0" />
            <span className="text-[9px] font-black uppercase tracking-widest leading-none mt-px">Chat</span>
          </button>
        )}

        {/* Team 1 Avatar */}
        <div 
          className={`relative transition-all duration-300 ${hoveredTeam === 1 ? 'scale-110' : 'hover:scale-105'}`}
          onMouseEnter={() => setHoveredTeam(1)}
          onMouseLeave={() => setHoveredTeam(null)}
        >
          <PairAvatar 
            p1Id={t1?.player1Id}
            p2Id={t1?.player2Id}
            p1Image={players?.find(p => p.id === t1?.player1Id)?.photoUrl}
            p2Image={players?.find(p => p.id === t1?.player2Id)?.photoUrl}
            onPlayerClick={onPlayerAvatarClick}
            className="w-11 h-11 md:w-14 md:h-14 shrink-0 transition-transform" 
          />
        </div>

        <div className="flex flex-col items-center justify-center">
          <span className="opacity-20 text-[10px] md:text-[11px] font-black italic tracking-tighter theme-primary-text">VS</span>
        </div>

        {/* Team 2 Avatar */}
        <div 
          className={`relative transition-all duration-300 ${hoveredTeam === 2 ? 'scale-110' : 'hover:scale-105'}`}
          onMouseEnter={() => setHoveredTeam(2)}
          onMouseLeave={() => setHoveredTeam(null)}
        >
          <PairAvatar 
            p1Id={t2?.player1Id}
            p2Id={t2?.player2Id}
            p1Image={players?.find(p => p.id === t2?.player1Id)?.photoUrl}
            p2Image={players?.find(p => p.id === t2?.player2Id)?.photoUrl}
            onPlayerClick={onPlayerAvatarClick}
            className="w-11 h-11 md:w-14 md:h-14 shrink-0 transition-transform" 
          />
        </div>

        {/* Close / Anular / Delete Button on Right (Vertically aligned with Avatars) */}
        {!isCompleted && (
          <div className="absolute right-1 z-20">
            {mode === 'player' ? (
              <button
                onClick={onDelete}
                className="px-2.5 py-1.5 bg-black/40 hover:bg-white/10 text-white/50 rounded-xl transition-all border border-white/5 text-[9px] font-black uppercase tracking-widest"
                title="Cancelar"
              >
                Cancelar
              </button>
            ) : confirmDelete ? (
              <div className="flex gap-1.5 items-center bg-red-500/90 backdrop-blur-md p-1 rounded-xl border border-red-400 shadow-xl scale-105">
                <span className="text-[8px] text-white font-black px-1">{m.status === 'disputed' ? '¿Desestimar?' : 'Verificando'}</span>
                <button onClick={() => setConfirmDelete(false)} className="bg-white text-red-600 px-2 py-0.5 rounded text-[9px] font-black uppercase shadow cursor-pointer">
                  Atrás
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 bg-black/40 hover:bg-red-500 hover:text-white text-white/50 rounded-xl transition-all border border-white/5 hover:scale-105 active:scale-95"
                title={m.status === 'disputed' ? "Desestimar Disputa" : "Anular Partido"}
              >
                <X size={15} />
              </button>
            )}
          </div>
        )}

        {readOnly && onDelete && (
          <div className="absolute right-1 z-20">
            {confirmDelete ? (
              <div className="flex gap-1.5 items-center bg-red-500/90 backdrop-blur-md p-1 rounded-xl border border-red-400 shadow-xl scale-105">
                <span className="text-[8px] text-white font-black px-1">¿Eliminar?</span>
                <button onClick={onDelete} className="bg-white text-red-600 px-2 py-0.5 rounded text-[9px] font-black uppercase shadow">
                  Sí
                </button>
                <button onClick={() => setConfirmDelete(false)} className="bg-black/40 text-white px-2 py-0.5 rounded text-[9px] font-bold uppercase">
                  No
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 bg-black/40 hover:bg-red-500 hover:text-white text-white/50 rounded-xl transition-all border border-white/5 hover:scale-105 active:scale-95"
                title="Eliminar Archivo"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        )}
      </div>

      {localError && (
        <div className="bg-red-500 text-white w-full text-center px-4 py-1.5 rounded-lg text-[9px] font-black shadow-xl animate-in slide-in-from-bottom-1 mt-1">
          {localError}
        </div>
      )}

      <div className="bg-black/20 p-2 md:p-2.5 rounded-xl border border-white/5 mt-1 w-full flex flex-col gap-1 relative shadow-inner">
        <div className={`flex items-center gap-3 px-1 mb-0.5 justify-start ${!readOnly ? 'pr-[58px] md:pr-[68px]' : ''}`}>
          <span className="flex-1 min-w-0 text-[8px] font-bold opacity-30 uppercase flex items-center gap-1 truncate">
            <div className={`w-1 h-1 rounded-full ${isCompleted ? 'bg-white/10' : 'theme-primary-bg opacity-50'}`} />
            {isCompleted ? 'Final' : 'Score'}
          </span>
          <div className="flex gap-1.5 shrink-0">
            <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20">S1</span>
            <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20">S2</span>
            <span className="w-7 md:w-9 text-center text-[7px] font-black opacity-20 transition-all duration-300" style={{ opacity: showSet3 ? 0.2 : 0.08 }}>S3</span>
          </div>
        </div>

        {m.impugnedReason && (
          <div className="mb-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <span className="text-[9px] text-red-400 font-bold uppercase tracking-widest block mb-1">Motivo de impugnación:</span>
            <p className="text-[10px] text-white/80 leading-tight italic mb-2">{m.impugnedReason}</p>
            {reportedWinnerTeamName && reportedScore && (
              <span className="text-[9px] text-white/50 block font-medium">
                Resultado original: gana {reportedWinnerTeamName} {reportedScore}
              </span>
            )}
          </div>
        )}

        <div className={`flex flex-col gap-1 w-full ${!readOnly ? 'pr-[58px] md:pr-[68px]' : ''}`}>
              {/* Team 1 Row */}
              <div className={`flex items-center justify-start relative gap-3 p-1 rounded-lg transition-all duration-300 ${hoveredTeam === 1 ? 'bg-white/5 translate-x-0.5' : ''}`}>
                <div className={`flex-1 min-w-0 transition-all duration-300 ${hoveredTeam === 1 ? 'opacity-100 theme-primary-text' : 'opacity-60'} text-[8px] md:text-[9.5px] font-black uppercase leading-none truncate`}>
                  {formatPairName(t1.name, 'minimal')}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {renderBox(1, 1)}
                  {renderBox(2, 1)}
                  {renderBox(3, 1)}
                </div>
              </div>

              {/* Team 2 Row */}
              <div className={`flex items-center justify-start relative gap-3 p-1 rounded-lg transition-all duration-300 ${hoveredTeam === 2 ? 'bg-white/5 translate-x-0.5' : ''}`}>
                <div className={`flex-1 min-w-0 transition-all duration-300 ${hoveredTeam === 2 ? 'opacity-100 theme-primary-text' : 'opacity-60'} text-[8px] md:text-[9.5px] font-black uppercase leading-none truncate`}>
                  {formatPairName(t2.name, 'minimal')}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {renderBox(1, 2)}
                  {renderBox(2, 2)}
                  {renderBox(3, 2)}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            {!readOnly && (
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex flex-col gap-1 z-10 w-[56px] md:w-[66px]">
                {/* Modificar Button */}
                {activeBox === null && (
                  <button
                    onClick={() => setActiveBox({ set: 1, team: 1 })}
                    disabled={isSaving || isSuccess}
                    className="h-[22px] md:h-[24px] w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black px-1 rounded-lg transition-all flex items-center justify-center shadow-sm active:scale-95 disabled:opacity-50 cursor-pointer"
                  >
                    <span className="text-[7.5px] md:text-[8px] uppercase tracking-widest leading-none">Modificar</span>
                  </button>
                )}

                {/* Save/Validar Button */}
                <button
                  onClick={handleConfirm}
                  disabled={isSaving || isSuccess || (activeBox !== null)}
                  className={`h-[40px] md:h-[46px] w-full ${
                    isSuccess 
                      ? (m.status === 'disputed' ? 'bg-emerald-600 text-white border-emerald-500' : 'theme-primary-bg')
                      : activeBox !== null 
                        ? 'bg-white/5 text-white/5 border-white/5' 
                        : (m.status === 'disputed' 
                            ? 'bg-emerald-500 hover:bg-emerald-400 text-white border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.3)]' 
                            : 'theme-primary-bg hover:opacity-80 text-black theme-primary-border/30')
                    } font-black px-1 rounded-xl transition-all flex flex-col items-center justify-center gap-0.5 border active:scale-95 disabled:grayscale-[0.5] shadow-md cursor-pointer`}
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 size={isSuccess ? 14 : 12} className="shrink-0" />
                      <span className="text-[8px] md:text-[8.5px] uppercase tracking-wider font-black leading-none text-center">
                        {isSuccess ? 'Listo' : (m.status === 'disputed' ? 'Validar' : 'Guardar')}
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Subtle Number Picker */}
            {!isCompleted && activeBox && (
              <>
                <div 
                  className="fixed inset-0 z-[140] cursor-default"
                  onClick={() => setActiveBox(null)}
                />
                <div className="mt-2 pt-2 border-t border-white/5 relative z-[150] animate-in slide-in-from-top-2 fade-in duration-200 bg-black/50 -mx-1.5 -mb-1.5 p-2 rounded-b-xl backdrop-blur-md">
                  <div className="flex justify-center gap-0.5 sm:gap-1.5">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                      <button
                        key={n}
                        onClick={() => handleNumberSelect(n)}
                        className="flex-1 max-w-[32px] h-8 md:max-w-[40px] md:h-10 bg-white/5 hover:bg-theme-primary/20 hover:scale-110 rounded-lg font-black text-xs md:text-sm transition-all duration-200 active:scale-95 border border-white/10 hover:theme-primary-border text-white shadow-lg"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Confirm Save Modal for Disputed Matches */}
            {confirmSave && m.status === 'disputed' && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                <div className="bg-zinc-900 border border-emerald-500/30 rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
                  <h4 className="text-lg font-black text-white uppercase tracking-wider mb-2">Confirmar Validación</h4>
                  <p className="text-white/60 text-sm mb-6">
                    Estás por validar y resolver la disputa con el resultado modificado. Esto sobreescribirá cualquier resultado previo y dará por finalizado el partido. ¿Confirmas esta acción?
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setConfirmSave(false)}
                      className="px-5 py-2.5 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl transition-all border border-white/10 text-sm cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={executeSave}
                      className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-wider rounded-xl transition-all text-sm shadow-md cursor-pointer"
                    >
                      Sí, Validar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirm Delete Full Modal */}
            {confirmDelete && mode !== 'player' && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-sm w-full text-center shadow-2xl">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3 border border-amber-500/20">
                    <Info size={24} className="text-amber-500" />
                  </div>
                  <h4 className="text-base font-black text-white uppercase tracking-wider mb-2">
                    {readOnly ? '¿Eliminar Archivo?' : (m.status === 'disputed' ? '¿Desestimar Disputa?' : '¿Anular Partido?')}
                  </h4>
                  {!readOnly && m.status !== 'disputed' && (
                    <p className="text-white/70 text-[11px] sm:text-xs mb-5 leading-relaxed bg-white/5 p-3 rounded-xl border border-white/5 text-left">
                      <strong className="text-white block mb-1 uppercase text-[10px]">Ten en cuenta:</strong>
                      En caso de penalización por no presentarse o mala conducta, puedes <b>cargar el resultado correspondiente</b>.<br/><br/>
                      Si decides anularlo, ninguna pareja recibirá puntaje ni estadística.
                    </p>
                  )}
                  {readOnly && (
                    <p className="text-white/60 text-sm mb-5">
                      ¿Seguro que deseas eliminar este registro del archivo? (El partido resuelto no se modificará ni se restablecerá nada).
                    </p>
                  )}
                  {!readOnly && m.status === 'disputed' && (
                    <p className="text-white/60 text-sm mb-5">
                      ¿Seguro que deseas desestimar esta disputa?
                    </p>
                  )}
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl transition-all border border-white/10 text-xs cursor-pointer"
                    >
                      Volver
                    </button>
                    <button
                      onClick={onDelete}
                      className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-black uppercase tracking-wider rounded-xl transition-all text-xs shadow-md cursor-pointer"
                    >
                      {readOnly ? 'Eliminar' : (m.status === 'disputed' ? 'Desestimar' : 'Anular')}
                    </button>
                  </div>
                </div>
              </div>
            )}
      </div>
    </div>
  );
}
